import { useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { useColor } from '@/contexts/ColorContext';
import { useSceneSettings } from '@/contexts/SceneSettingsContext';
import { ColorInput } from '@/components/parameter/ColorInput';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HexColorPicker } from 'react-colorful';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/** Small pill shown next to a label when the value is driven by SCAD code. */
function CodeBadge() {
  return (
    <span className="ml-1.5 inline-block rounded border border-sky-500/30 px-1 font-mono text-[9px] leading-tight text-sky-500 dark:text-sky-400">
      code
    </span>
  );
}

function SettingRow({
  label,
  children,
  overridden,
}: {
  label: string;
  children: React.ReactNode;
  overridden?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs text-muted-foreground">
        {label}
        {overridden && <CodeBadge />}
      </span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function SliderSetting({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  defaultValue,
  overridden,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  overridden?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {label}
          {overridden && <CodeBadge />}
        </span>
        <span className="text-xs tabular-nums text-foreground">
          {value.toFixed(2)}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        defaultValue={defaultValue !== undefined ? [defaultValue] : undefined}
        className="h-6"
      />
    </div>
  );
}

function floatToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function hexToFloats(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const fmt = (n: number) => parseFloat(n.toFixed(3));
  return [fmt(r), fmt(g), fmt(b)];
}

function ColorSwatchSetting({
  label,
  color,
  onChange,
}: {
  label: string;
  color: [number, number, number];
  onChange: (color: [number, number, number]) => void;
}) {
  const hex = floatToHex(color[0], color[1], color[2]);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 rounded-md px-1.5 py-0.5 text-xs transition-colors hover:bg-muted">
              <div
                className="h-3 w-3 rounded-full shadow-sm"
                style={{ backgroundColor: hex }}
              />
              <span className="font-mono text-[10px] uppercase text-muted-foreground">
                {hex}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-auto rounded-md border-border bg-background p-4 shadow-md"
            style={{ minWidth: '200px' }}
          >
            <HexColorPicker
              color={hex}
              onChange={(c) => onChange(hexToFloats(c))}
              style={{ height: '120px', width: '100%' }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

interface ModelColorSectionProps {
  /** Hide the Model Color row when the compiled model uses only explicit color() calls. */
  showModelColor?: boolean;
}

export function ModelColorSection({
  showModelColor = true,
}: ModelColorSectionProps) {
  const { color, setColor } = useColor();
  const {
    settings,
    sceneOverrides,
    updateMaterial,
    updateLight,
    updateShadow,
    updateSSAO,
    updateBloom,
    resetToDefaults,
  } = useSceneSettings();

  // Shorthand helpers for badge visibility
  const om = sceneOverrides.material;
  const odl = sceneOverrides.defaultLights;
  const osh = sceneOverrides.shadows;
  const obl = sceneOverrides.bloom;
  const oao = sceneOverrides.ssao;
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Main Color Picker — only shown when the fallback color is actually used */}
      {showModelColor && (
        <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
          <span className="text-sm font-medium text-foreground">
            Model Color
          </span>
          <ColorInput compact value={color} onChange={setColor} />
        </div>
      )}

      {/* Advanced Options Collapsible */}
      <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          {isAdvancedOpen ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          <span>Advanced Scene Settings</span>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-3 space-y-4">
          {/* Material Settings */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">
                Material
              </span>
            </div>
            <div className="space-y-3 rounded-md bg-muted/30 p-3">
              {/* Material Type Toggle */}
              <SettingRow
                label="Physical Material"
                overridden={om?.usePhysicalMaterial !== undefined}
              >
                <div className="flex justify-end">
                  <Switch
                    checked={settings.material.usePhysicalMaterial}
                    onCheckedChange={(v) =>
                      updateMaterial({ usePhysicalMaterial: v })
                    }
                  />
                </div>
              </SettingRow>

              {/* Standard properties (always shown) */}
              <SliderSetting
                label="Metalness"
                value={settings.material.metalness}
                onChange={(v) => updateMaterial({ metalness: v })}
                defaultValue={0}
                overridden={om?.metalness !== undefined}
              />
              <SliderSetting
                label="Roughness"
                value={settings.material.roughness}
                onChange={(v) => updateMaterial({ roughness: v })}
                defaultValue={0.8}
                overridden={om?.roughness !== undefined}
              />
              <SliderSetting
                label="Environment Reflection"
                value={settings.material.envMapIntensity}
                onChange={(v) => updateMaterial({ envMapIntensity: v })}
                defaultValue={0.2}
                overridden={om?.envMapIntensity !== undefined}
              />
              <SettingRow
                label="Flat Shading"
                overridden={om?.flatShading !== undefined}
              >
                <div className="flex justify-end">
                  <Switch
                    checked={settings.material.flatShading}
                    onCheckedChange={(v) => updateMaterial({ flatShading: v })}
                  />
                </div>
              </SettingRow>

              {/* Physical material properties (only shown when physical material is enabled) */}
              {settings.material.usePhysicalMaterial && (
                <>
                  <div className="mt-1 border-t border-border/40 pt-2">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Physical Properties
                    </span>
                  </div>

                  {/* Clearcoat */}
                  <SliderSetting
                    label="Clearcoat"
                    value={settings.material.clearcoat}
                    onChange={(v) => updateMaterial({ clearcoat: v })}
                    defaultValue={0}
                    overridden={om?.clearcoat !== undefined}
                  />
                  {settings.material.clearcoat > 0 && (
                    <SliderSetting
                      label="Clearcoat Roughness"
                      value={settings.material.clearcoatRoughness}
                      onChange={(v) =>
                        updateMaterial({ clearcoatRoughness: v })
                      }
                      defaultValue={0}
                      overridden={om?.clearcoatRoughness !== undefined}
                    />
                  )}

                  {/* Sheen */}
                  <SliderSetting
                    label="Sheen"
                    value={settings.material.sheen}
                    onChange={(v) => updateMaterial({ sheen: v })}
                    defaultValue={0}
                    overridden={om?.sheen !== undefined}
                  />
                  {settings.material.sheen > 0 && (
                    <>
                      <SliderSetting
                        label="Sheen Roughness"
                        value={settings.material.sheenRoughness}
                        onChange={(v) => updateMaterial({ sheenRoughness: v })}
                        defaultValue={0.5}
                        overridden={om?.sheenRoughness !== undefined}
                      />
                      <ColorSwatchSetting
                        label="Sheen Color"
                        color={settings.material.sheenColor}
                        onChange={(c) => updateMaterial({ sheenColor: c })}
                      />
                    </>
                  )}

                  {/* Iridescence */}
                  <SliderSetting
                    label="Iridescence"
                    value={settings.material.iridescence}
                    onChange={(v) => updateMaterial({ iridescence: v })}
                    defaultValue={0}
                    overridden={om?.iridescence !== undefined}
                  />
                  {settings.material.iridescence > 0 && (
                    <SliderSetting
                      label="Iridescence IOR"
                      value={settings.material.iridescenceIOR}
                      onChange={(v) => updateMaterial({ iridescenceIOR: v })}
                      min={1}
                      max={2.333}
                      defaultValue={1.3}
                      overridden={om?.iridescenceIOR !== undefined}
                    />
                  )}

                  {/* Transmission */}
                  <SliderSetting
                    label="Transmission"
                    value={settings.material.transmission}
                    onChange={(v) => updateMaterial({ transmission: v })}
                    defaultValue={0}
                    overridden={om?.transmission !== undefined}
                  />
                  {settings.material.transmission > 0 && (
                    <>
                      <SliderSetting
                        label="IOR"
                        value={settings.material.ior}
                        onChange={(v) => updateMaterial({ ior: v })}
                        min={1}
                        max={2.333}
                        defaultValue={1.5}
                        overridden={om?.ior !== undefined}
                      />
                      <SliderSetting
                        label="Thickness"
                        value={settings.material.thickness}
                        onChange={(v) => updateMaterial({ thickness: v })}
                        min={0}
                        max={10}
                        step={0.1}
                        defaultValue={0}
                        overridden={om?.thickness !== undefined}
                      />
                      <ColorSwatchSetting
                        label="Attenuation Color"
                        color={settings.material.attenuationColor}
                        onChange={(c) =>
                          updateMaterial({ attenuationColor: c })
                        }
                      />
                      <SliderSetting
                        label="Attenuation Distance"
                        value={settings.material.attenuationDistance}
                        onChange={(v) =>
                          updateMaterial({ attenuationDistance: v })
                        }
                        min={0}
                        max={100}
                        step={0.5}
                        defaultValue={0}
                        overridden={om?.attenuationDistance !== undefined}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Light Settings */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-foreground">
              Lighting
            </span>
            <div className="space-y-3 rounded-md bg-muted/30 p-3">
              <SliderSetting
                label="Ambient Light"
                value={settings.light.ambientIntensity}
                onChange={(v) => updateLight({ ambientIntensity: v })}
                max={4}
                defaultValue={0.4}
                overridden={odl?.ambient !== undefined}
              />
              <SliderSetting
                label="Key Light"
                value={settings.light.keyLightIntensity}
                onChange={(v) => updateLight({ keyLightIntensity: v })}
                max={3}
                defaultValue={1.0}
                overridden={odl?.key !== undefined}
              />
              <SliderSetting
                label="Fill Light"
                value={settings.light.fillLightIntensity}
                onChange={(v) => updateLight({ fillLightIntensity: v })}
                max={2}
                defaultValue={0.3}
                overridden={odl?.fill !== undefined}
              />
              <SliderSetting
                label="Rim Light"
                value={settings.light.rimLightIntensity}
                onChange={(v) => updateLight({ rimLightIntensity: v })}
                max={2}
                defaultValue={0.2}
                overridden={odl?.rim !== undefined}
              />
            </div>
          </div>

          {/* Shadow Settings */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-foreground">Shadows</span>
            <div className="space-y-3 rounded-md bg-muted/30 p-3">
              <SettingRow
                label="Enable Shadows"
                overridden={osh?.enabled !== undefined}
              >
                <div className="flex justify-end">
                  <Switch
                    checked={settings.shadow.enabled}
                    onCheckedChange={(v) => updateShadow({ enabled: v })}
                  />
                </div>
              </SettingRow>
              {settings.shadow.enabled && (
                <>
                  <SliderSetting
                    label="Shadow Intensity"
                    value={settings.shadow.intensity}
                    onChange={(v) => updateShadow({ intensity: v })}
                    defaultValue={0.3}
                    overridden={osh?.intensity !== undefined}
                  />
                  <SettingRow label="Shadow Quality">
                    <Select
                      value={settings.shadow.mapSize.toString()}
                      onValueChange={(v) =>
                        updateShadow({ mapSize: parseInt(v) })
                      }
                    >
                      <SelectTrigger className="h-7 w-24 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="512">Low</SelectItem>
                        <SelectItem value="1024">Medium</SelectItem>
                        <SelectItem value="2048">High</SelectItem>
                        <SelectItem value="4096">Ultra</SelectItem>
                      </SelectContent>
                    </Select>
                  </SettingRow>
                </>
              )}
            </div>
          </div>

          {/* Ambient Occlusion (SSAO) Settings */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-foreground">
              Ambient Occlusion
            </span>
            <div className="space-y-3 rounded-md bg-muted/30 p-3">
              <SettingRow
                label="Enable SSAO"
                overridden={oao?.enabled !== undefined}
              >
                <div className="flex justify-end">
                  <Switch
                    checked={settings.ssao.enabled}
                    onCheckedChange={(v) => updateSSAO({ enabled: v })}
                  />
                </div>
              </SettingRow>
              {settings.ssao.enabled && (
                <>
                  <SliderSetting
                    label="Intensity"
                    value={settings.ssao.intensity}
                    onChange={(v) => updateSSAO({ intensity: v })}
                    min={0}
                    max={4}
                    step={0.1}
                    defaultValue={1.5}
                    overridden={oao?.intensity !== undefined}
                  />
                  <SliderSetting
                    label="Radius"
                    value={settings.ssao.aoRadius}
                    onChange={(v) => updateSSAO({ aoRadius: v })}
                    min={0}
                    max={2}
                    step={0.01}
                    defaultValue={0.5}
                    overridden={oao?.aoRadius !== undefined}
                  />
                  <SliderSetting
                    label="Luminance Influence"
                    value={settings.ssao.luminanceInfluence}
                    onChange={(v) => updateSSAO({ luminanceInfluence: v })}
                    min={0}
                    max={1}
                    step={0.01}
                    defaultValue={0.7}
                    overridden={oao?.luminanceInfluence !== undefined}
                  />
                  <SettingRow label="Samples">
                    <Select
                      value={settings.ssao.samples.toString()}
                      onValueChange={(v) =>
                        updateSSAO({ samples: parseInt(v) })
                      }
                    >
                      <SelectTrigger className="h-7 w-24 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">Low (8)</SelectItem>
                        <SelectItem value="16">Medium (16)</SelectItem>
                        <SelectItem value="24">High (24)</SelectItem>
                        <SelectItem value="32">Ultra (32)</SelectItem>
                      </SelectContent>
                    </Select>
                  </SettingRow>
                </>
              )}
            </div>
          </div>

          {/* Bloom Settings */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-foreground">
              Bloom (Glow)
            </span>
            <div className="space-y-3 rounded-md bg-muted/30 p-3">
              <SettingRow
                label="Enable Bloom"
                overridden={obl?.enabled !== undefined}
              >
                <div className="flex justify-end">
                  <Switch
                    checked={settings.bloom.enabled}
                    onCheckedChange={(v) => updateBloom({ enabled: v })}
                  />
                </div>
              </SettingRow>
              {settings.bloom.enabled && (
                <>
                  <SliderSetting
                    label="Intensity"
                    value={settings.bloom.intensity}
                    onChange={(v) => updateBloom({ intensity: v })}
                    min={0}
                    max={5}
                    step={0.05}
                    defaultValue={1.0}
                    overridden={obl?.intensity !== undefined}
                  />
                  <SliderSetting
                    label="Threshold"
                    value={settings.bloom.luminanceThreshold}
                    onChange={(v) => updateBloom({ luminanceThreshold: v })}
                    min={0}
                    max={1}
                    step={0.01}
                    defaultValue={0.9}
                    overridden={obl?.luminanceThreshold !== undefined}
                  />
                  <SliderSetting
                    label="Smoothing"
                    value={settings.bloom.luminanceSmoothing}
                    onChange={(v) => updateBloom({ luminanceSmoothing: v })}
                    min={0}
                    max={1}
                    step={0.005}
                    defaultValue={0.025}
                    overridden={obl?.luminanceSmoothing !== undefined}
                  />
                  <SettingRow
                    label="Mipmap Blur"
                    overridden={obl?.mipmapBlur !== undefined}
                  >
                    <div className="flex justify-end">
                      <Switch
                        checked={settings.bloom.mipmapBlur}
                        onCheckedChange={(v) => updateBloom({ mipmapBlur: v })}
                      />
                    </div>
                  </SettingRow>
                </>
              )}
            </div>
          </div>

          {/* Reset Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={resetToDefaults}
                >
                  <RotateCcw className="mr-2 h-3 w-3" />
                  Reset to Defaults
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset all scene settings to default values</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
